// src/components/ShareButton.jsx
// src/components/ShareButton.jsx
import React, { useState } from "react";
import { 
  FiShare2, 
  FiCheck, 
  FiLink,
  FiX
} from "react-icons/fi";
import { FaWhatsapp, FaInstagram, FaTelegram, FaFacebook, FaTwitter } from "react-icons/fa";

const ShareButton = ({ product, className = "" }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const getProductLink = () => {
    if (!product) return window.location.href;
    const productId = product._id || product.id;
    return `${window.location.origin}/product/${productId}`;
  };

  const getShareText = () => {
    if (!product) return "Check out this amazing product!";
    
    let text = `${product.name}`;
    if (product.price) {
      text += ` - ₹${product.price}`;
    }
    if (product.originalPrice && product.originalPrice > product.price) {
      const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
      text += ` (${discount}% OFF)`;
    }
    return text;
  };

  const handleCopyLink = () => {
    try {
      const link = getProductLink();
      navigator.clipboard.writeText(link);
      setCopied(true);
      setError("");
      setTimeout(() => {
        setCopied(false);
        setShowShareModal(false);
      }, 1500);
    } catch (err) {
      setError("Failed to copy link");
    }
  };

  const shareOnWhatsApp = () => {
    try {
      const text = getShareText();
      const link = getProductLink();
      const url = `https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`;
      window.open(url, '_blank');
      setShowShareModal(false);
      setError("");
    } catch (err) {
      setError("Failed to share on WhatsApp");
    }
  };

  const shareOnInstagram = () => {
    try {
      const text = getShareText();
      const link = getProductLink();
      const instaText = `${text}\n\n${link}`;
      navigator.clipboard.writeText(instaText);
      setCopied(true);
      setError("");
      setTimeout(() => {
        setCopied(false);
        setShowShareModal(false);
      }, 1500);
      
      // Instagram app open karne ka try karo (mobile pe)
      if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        window.location.href = `instagram://share?text=${encodeURIComponent(instaText)}`;
      }
    } catch (err) {
      setError("Failed to copy text for Instagram");
    }
  };

  const shareOnTelegram = () => {
    try {
      const text = getShareText();
      const link = getProductLink();
      const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
      setShowShareModal(false);
      setError("");
    } catch (err) {
      setError("Failed to share on Telegram");
    }
  };

  const shareOnFacebook = () => {
    try {
      const link = getProductLink();
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
      window.open(url, '_blank');
      setShowShareModal(false);
      setError("");
    } catch (err) {
      setError("Failed to share on Facebook");
    }
  };

  const shareOnTwitter = () => {
    try {
      const text = getShareText();
      const link = getProductLink();
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`;
      window.open(url, '_blank');
      setShowShareModal(false);
      setError("");
    } catch (err) {
      setError("Failed to share on Twitter");
    }
  };

  const handleNativeShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product?.name || "Product",
          text: getShareText(),
          url: getProductLink(),
        });
        // Agar user ne share successful kiya
        setError("");
      } else {
        // Desktop pe modal show karo
        setShowShareModal(true);
        setError("");
      }
    } catch (err) {
      // User ne cancel kar diya ya error aaya
      if (err.name !== 'AbortError') {
        // Sirf abort error nahi hai tab hi error show karo
        setError("");
      }
      // Cancel error ko ignore karo
    }
  };

  const closeModal = () => {
    setShowShareModal(false);
    setError("");
    setCopied(false);
  };

  return (
    <>
      {/* Main Share Button */}
      <button
        onClick={handleNativeShare}
        className={`w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:text-red-500 hover:bg-red-50 transition-colors ${className}`}
        title="Share"
        aria-label="Share product"
      >
        <FiShare2 size={18} />
      </button>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 max-w-sm w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-900">Share Product</h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 p-1"
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-3 p-2 bg-red-50 text-red-600 text-xs rounded-lg">
                {error}
              </div>
            )}

            {/* Success Message */}
            {copied && (
              <div className="mb-3 p-2 bg-green-50 text-green-600 text-xs rounded-lg">
                Link copied to clipboard!
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 mb-4">
              {/* WhatsApp */}
              <button
                onClick={shareOnWhatsApp}
                className="flex flex-col items-center p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                aria-label="Share on WhatsApp"
              >
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-2">
                  <FaWhatsapp className="text-white" size={24} />
                </div>
                <span className="text-xs font-medium">WhatsApp</span>
              </button>

              {/* Instagram */}
              <button
                onClick={shareOnInstagram}
                className="flex flex-col items-center p-3 bg-pink-50 hover:bg-pink-100 rounded-lg transition-colors"
                aria-label="Share on Instagram"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-2">
                  <FaInstagram className="text-white" size={24} />
                </div>
                <span className="text-xs font-medium">Instagram</span>
              </button>

              {/* Telegram */}
              <button
                onClick={shareOnTelegram}
                className="flex flex-col items-center p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                aria-label="Share on Telegram"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-2">
                  <FaTelegram className="text-white" size={24} />
                </div>
                <span className="text-xs font-medium">Telegram</span>
              </button>

              {/* Facebook */}
              <button
                onClick={shareOnFacebook}
                className="flex flex-col items-center p-3 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                aria-label="Share on Facebook"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-2">
                  <FaFacebook className="text-white" size={24} />
                </div>
                <span className="text-xs font-medium">Facebook</span>
              </button>

              {/* Twitter */}
              <button
                onClick={shareOnTwitter}
                className="flex flex-col items-center p-3 bg-sky-50 hover:bg-sky-100 rounded-lg transition-colors"
                aria-label="Share on Twitter"
              >
                <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center mb-2">
                  <FaTwitter className="text-white" size={24} />
                </div>
                <span className="text-xs font-medium">Twitter</span>
              </button>

              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="flex flex-col items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Copy link"
              >
                <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mb-2">
                  {copied ? (
                    <FiCheck className="text-white" size={24} />
                  ) : (
                    <FiLink className="text-white" size={24} />
                  )}
                </div>
                <span className="text-xs font-medium">
                  {copied ? "Copied!" : "Copy Link"}
                </span>
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              Share this product with friends
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButton;